import { ThemedLayout, ThemedSider, useNotificationProvider } from "@refinedev/antd";
import "@refinedev/antd/dist/reset.css";
import "styles/table.css";
import { Authenticated, ErrorComponent, Refine } from "@refinedev/core";
import routerProvider, { NavigateToResource } from "@refinedev/react-router";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { ConfigProvider, App as AntdApp } from "antd";
import axios, { InternalAxiosRequestConfig } from "axios";

import { ThemeProvider, useTheme } from "contexts/ThemeContext";
import { siderTheme } from "config/theme";

import { Header, Title } from "components/layout";
import { authProvider, isSafeRedirect } from "./authProvider";

import {
  FaAddressCard,
  FaAward,
  FaBell,
  FaBook,
  FaBookOpen,
  FaBuilding,
  FaCartPlus,
  FaChartBar,
  FaCity,
  FaClipboardList,
  FaCloudDownloadAlt,
  FaCog,
  FaCogs,
  FaCoins,
  FaDatabase,
  FaEllipsisH,
  FaEnvelope,
  FaEnvelopeOpenText,
  FaExchangeAlt,
  FaExclamationTriangle,
  FaFileAlt,
  FaFileSignature,
  FaFileUpload,
  FaFlag,
  FaGlobeAmericas,
  FaHistory,
  FaHome,
  FaIdBadge,
  FaMap,
  FaMapMarkedAlt,
  FaMoneyCheckAlt,
  FaNewspaper,
  FaRoute,
  FaShip,
  FaShoppingCart,
  FaToolbox,
  FaUpload,
  FaUserLock,
  FaUserTie,
} from "react-icons/fa";

import { accessControlProvider } from "accessControlProvider";
import { dataProvider } from "dataProvider";
import {
  AddressCreate,
  AddressEdit,
  AddressList,
  AddressShow,
} from "pages/address";
import {
  MetadataAdpCreate,
  MetadataAdpEdit,
  MetadataAdpList,
  MetadataAdpShow,
} from "pages/adp-service";
import {
  MetadataArcsCreate,
  MetadataArcsEdit,
  MetadataArcsList,
  MetadataArcsShow,
} from "pages/arcs-service";
import {
  MetadataAvcsCreate,
  MetadataAvcsEdit,
  MetadataAvcsList,
  MetadataAvcsShow,
} from "pages/avcs-service";
import {
  ContactCreate,
  ContactEdit,
  ContactList,
  ContactShow,
} from "pages/contact";
import {
  CountryCreate,
  CountryEdit,
  CountryList,
  CountryShow,
} from "pages/country";
import {
  CurrencyCreate,
  CurrencyEdit,
  CurrencyList,
  CurrencyShow,
} from "pages/currency";
import {
  GlobalSettingsPage,
  UploadFetchPermitsPage,
  UploadOrdersPage,
  UploadSeallPermitsPage,
  UploadVesselPermitsPage,
} from "pages/custom";
import { UploadOrdersMultipartPage } from "pages/custom/upload-orders-multipart";
import { VesselEmailSettingsPage } from "pages/custom/vessel-email-settings";
import { Dashboard } from "pages/dashboard";
import {
  DigitalPublicationCreate,
  DigitalPublicationList,
} from "pages/digital-publication";
import { EcdisCreate, EcdisEdit, EcdisList, EcdisShow } from "pages/ecdis";
import {
  MetadataEnpCreate,
  MetadataEnpEdit,
  MetadataEnpList,
  MetadataEnpShow,
} from "pages/enp-service";
import {
  FlagStateCircularCreate,
  FlagStateCircularList,
} from "pages/flag-circular";
import { InvoiceReportList } from "pages/invoice";
import { InvoiceDataCreate, InvoiceDataList } from "pages/invoice-data";
import { UkhoInvoiceReportList } from "pages/ukho-invoice-report";
import { OrderReportList } from "pages/order-report";
import { OnDemandReportsList } from "pages/on-demand-reports";
import {
  LicenceCreate,
  LicenceEdit,
  LicenceList,
  LicenceShow,
} from "pages/licence";
import { Login } from "pages/login";
import { NavWarningsCreate, NavWarningsList } from "pages/nav-warnings";
import {
  NoticesMarinersCreate,
  NoticesMarinersList,
} from "pages/notices-mariners";
import { OrderList, OrderShow } from "pages/order";
import { OrderUploadStatusList } from "pages/order-upload-status/list";
import {
  MetadataOtherCreate,
  MetadataOtherEdit,
  MetadataOtherList,
  MetadataOtherShow,
} from "pages/other-service";
import { PhysicalBooksCreate, PhysicalBooksList } from "pages/physical-books";
import { ShipcoCreate, ShipcoEdit, ShipcoList, ShipcoShow } from "pages/shipco";
import {
  ShipcoGroupCreate,
  ShipcoGroupEdit,
  ShipcoGroupList,
  ShipcoGroupShow,
} from "pages/shipco-group";
import {
  SubscriptionCreate,
  SubscriptionEdit,
  SubscriptionList,
  SubscriptionShow,
} from "pages/subscription";
import { VesselCreate, VesselEdit, VesselList, VesselShow } from "pages/vessel";
import {
  VesselCertificatesCreate,
  VesselCertificatesList,
} from "pages/vessel-certificates";
import {
  EntmVesselsCreate,
  EntmVesselsEdit,
  EntmVesselsList,
  EntmVesselsShow,
} from "pages/entm-vessels";
import {
  BulkEmailJobsList,
  BulkEmailJobsShow,
} from "pages/bulk-email-jobs";
import { VesselSpendReportList } from "pages/vessel-spend-report";
import { VesselResourcesList } from "pages/vessel-resources";
import { UploadDataTransferPage } from "pages/custom/upload-data-transfer";
import { VesselStartupKitList } from "pages/vessel-starter-kit";
import { VesselStartupKitCreate } from "pages/vessel-starter-kit/create";
import { UploadAvcsIncrementalUpdatePage } from "pages/custom/upload-incremental-update";
import { IncrementalUpdateList } from "pages/incremental-update";
import { IncrementalUpdateCreate } from "pages/incremental-update/create";
import { VesselDataTransferCreate } from "pages/vessel-data-transfer/create";
import { VesselDataTransferList } from "pages/vessel-data-transfer";
import { VesselResourceCreate } from "pages/vessel-resources/create";
import { HoldingsReportPage } from "pages/vessel-resources/holdings-report";
import VesselAccounts from "pages/custom/vessel-accounts";
import { EnpDataList } from "pages/enp-data";
import {
  VesselTransferList,
  VesselTransferShow,
  VesselTransferCreate,
} from "pages/vessel-transfer";
import { AuditLogList } from "./pages/audit-logs/list";
import { VesselDataVerificationsList, VesselDataVerificationApprove } from "pages/vessel-data-verifications";
import { AuditOutlined } from "@ant-design/icons";

const TOKEN_KEY = "emr-auth";

const axiosInstance = axios.create();

axiosInstance.interceptors.request.use((request: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    request.headers.set("Authorization", `Bearer ${token}`);
  }
  return request;
});

const LoginRedirect = () => {
  const { pathname, search } = useLocation();
  const redirect = encodeURIComponent(pathname + search);
  return <Navigate to={`/login?redirect=${redirect}`} replace />;
};

const AlreadyAuthedRedirect = () => {
  const [params] = useSearchParams();
  const redirect = params.get("redirect");
  return isSafeRedirect(redirect) ? <Navigate to={redirect} replace /> : <NavigateToResource resource="dashboard" />;
};

function ThemedApp() {
  const { theme } = useTheme();

  return (
    <ConfigProvider theme={theme}>
      <AntdApp>
        <BrowserRouter>
          <Refine
            routerProvider={routerProvider}
            resources={[
              {
                name: "dashboard",
                list: "/dashboard",
                meta: { label: "Dashboard", icon: <FaHome /> },
              },
              {
                name: "emr-operations",
                meta: { label: "Operations", icon: <FaCartPlus /> },
              },
              {
                name: "emr-fleet",
                meta: { label: "Fleet", icon: <FaShip /> },
              },
              {
                name: "emr-subscription",
                meta: { label: "Subscriptions", icon: <FaFileSignature /> },
              },
              {
                name: "emr-report",
                meta: { label: "Reports", icon: <FaChartBar /> },
              },
              {
                name: "emr-bookshelf",
                meta: { label: "Bookshelf", icon: <FaBook /> },
              },
              {
                name: "emr-vessel-services",
                meta: { label: "Vessel Services", icon: <FaToolbox /> },
              },
              {
                name: "emr-settings",
                meta: { label: "Settings", icon: <FaCogs /> },
              },
              {
                name: "upload-orders",
                list: "/upload-orders",
                meta: { parent: "emr-operations", label: "Upload Orders", icon: <FaFileUpload /> },
              },
              {
                name: "order",
                list: "/order",
                show: "/order/show/:id",
                meta: { parent: "emr-operations", label: "Orders", icon: <FaShoppingCart /> },
              },
              {
                name: "order-upload-status",
                list: "/order-upload-status",
                meta: { parent: "emr-operations", label: "Upload Status", icon: <FaHistory /> },
              },
              {
                name: "upload-vessel-permits",
                list: "/upload-vessel-permits",
                meta: { parent: "emr-operations", label: "Vessel Permits", icon: <FaUpload /> },
              },
              {
                name: "upload-seall-permits",
                list: "/upload-seall-permits",
                meta: { parent: "emr-operations", label: "Seall Permits", icon: <FaUpload /> },
              },
              {
                name: "upload-fetch-permits",
                list: "/upload-fetch-permits",
                meta: { parent: "emr-operations", label: "Fetch Permits", icon: <FaUpload /> },
              },
              {
                name: "shipco",
                list: "/shipco",
                create: "/shipco/create",
                edit: "/shipco/edit/:id",
                show: "/shipco/show/:id",
                meta: { parent: "emr-fleet", icon: <FaBuilding /> },
              },
              {
                name: "shipco-group",
                list: "/shipco-group",
                create: "/shipco-group/create",
                edit: "/shipco-group/edit/:id",
                show: "/shipco-group/show/:id",
                meta: { parent: "emr-fleet", label: "Shipco Groups", icon: <FaCity /> },
              },
              {
                name: "licence",
                list: "/licence",
                create: "/licence/create",
                edit: "/licence/edit/:id",
                show: "/licence/show/:id",
                meta: { parent: "emr-fleet", label: "Licences", icon: <FaIdBadge /> },
              },
              {
                name: "vessel",
                list: "/vessel",
                create: "/vessel/create",
                edit: "/vessel/edit/:id",
                show: "/vessel/show/:id",
                meta: { parent: "emr-fleet", icon: <FaShip /> },
              },
              {
                name: "ecdis",
                list: "/ecdis",
                create: "/ecdis/create",
                edit: "/ecdis/edit/:id",
                show: "/ecdis/show/:id",
                meta: { parent: "emr-fleet", label: "Ecdis", icon: <FaRoute /> },
              },
              {
                name: "entm-vessels",
                list: "/entm-vessels",
                create: "/entm-vessels/create",
                edit: "/entm-vessels/edit/:id",
                show: "/entm-vessels/show/:id",
                meta: { parent: "emr-fleet", label: "eNTM Vessels", icon: <FaShip /> },
              },
              {
                name: "vessel-transfer",
                list: "/vessel-transfer",
                create: "/vessel-transfer/create",
                show: "/vessel-transfer/show/:id",
                meta: { parent: "emr-fleet", label: "Vessel Transfer", icon: <FaExchangeAlt /> },
              },
              {
                name: "bulk-email",
                list: "/bulk-email",
                show: "/bulk-email/show/:id",
                meta: { parent: "emr-fleet", label: "Bulk Emails", icon: <FaEnvelopeOpenText /> },
              },
              {
                name: "subscription",
                list: "/subscription",
                create: "/subscription/create",
                edit: "/subscription/edit/:id",
                show: "/subscription/show/:id",
                meta: { parent: "emr-subscription", label: "Subscriptions", icon: <FaFileSignature /> },
              },
              {
                name: "metadata-avcs",
                list: "/metadata-avcs",
                create: "/metadata-avcs/create",
                edit: "/metadata-avcs/edit/:id",
                show: "/metadata-avcs/show/:id",
                meta: { parent: "emr-subscription", label: "AVCS Service", icon: <FaMap /> },
              },
              {
                name: "metadata-adp",
                list: "/metadata-adp",
                create: "/metadata-adp/create",
                edit: "/metadata-adp/edit/:id",
                show: "/metadata-adp/show/:id",
                meta: { parent: "emr-subscription", label: "ADP Service", icon: <FaFileAlt /> },
              },
              {
                name: "metadata-enp",
                list: "/metadata-enp",
                create: "/metadata-enp/create",
                edit: "/metadata-enp/edit/:id",
                show: "/metadata-enp/show/:id",
                meta: { parent: "emr-subscription", label: "ENP Service", icon: <FaNewspaper /> },
              },
              {
                name: "metadata-arcs",
                list: "/metadata-arcs",
                create: "/metadata-arcs/create",
                edit: "/metadata-arcs/edit/:id",
                show: "/metadata-arcs/show/:id",
                meta: { parent: "emr-subscription", label: "ARCS Service", icon: <FaMapMarkedAlt /> },
              },
              {
                name: "metadata-other",
                list: "/metadata-other",
                create: "/metadata-other/create",
                edit: "/metadata-other/edit/:id",
                show: "/metadata-other/show/:id",
                meta: { parent: "emr-subscription", label: "Other Services", icon: <FaEllipsisH /> },
              },
              {
                name: "ukho-invoice-report",
                list: "/ukho-invoice-report",
                meta: { parent: "emr-report", label: "UKHO Invoices", icon: <FaMoneyCheckAlt /> },
              },
              {
                name: "order-report",
                list: "/order-report",
                meta: { parent: "emr-report", label: "Order Report", icon: <FaShoppingCart /> },
              },
              {
                name: "on-demand-reports",
                list: "/on-demand-reports",
                meta: { parent: "emr-report", label: "On-Demand Reports", icon: <FaChartBar /> },
              },
              {
                name: "digital-publication",
                list: "/digital-publication",
                create: "/digital-publication/create",
                meta: { parent: "emr-bookshelf", label: "eMariner Publications", icon: <FaBookOpen /> },
              },
              {
                name: "enp-data",
                list: "/enp-data",
                meta: { parent: "emr-bookshelf", label: "e-NP Data", icon: <FaDatabase /> },
              },
              {
                name: "flag-circular",
                list: "/flag-circular",
                create: "/flag-circular/create",
                meta: { parent: "emr-bookshelf", label: "Flag Circulars", icon: <FaFlag /> },
              },
              {
                name: "notices-mariners",
                list: "/notices-mariners",
                create: "/notices-mariners/create",
                meta: { parent: "emr-bookshelf", label: "Notices to Mariners", icon: <FaBell /> },
              },
              {
                name: "nav-warnings",
                list: "/nav-warnings",
                create: "/nav-warnings/create",
                meta: { parent: "emr-bookshelf", label: "Navigational Warnings", icon: <FaExclamationTriangle /> },
              },
              {
                name: "physical-books",
                list: "/physical-books",
                create: "/physical-books/create",
                meta: { parent: "emr-bookshelf", label: "SL Publications", icon: <FaBook /> },
              },
              {
                name: "vessel-certificates",
                list: "/vessel-certificates",
                create: "/vessel-certificates/create",
                meta: { parent: "emr-bookshelf", label: "Certificates", icon: <FaAward /> },
              },
              {
                name: "vessel-resource",
                list: "/vessel-resource",
                create: "/vessel-resource/create",
                meta: { parent: "emr-vessel-services", label: "Permits & Data", icon: <FaClipboardList /> },
              },
              {
                name: "vessel-startup-kit",
                list: "/vessel-startup-kit",
                create: "/vessel-startup-kit/create",
                meta: { parent: "emr-vessel-services", label: "Vessel Startup Kit", icon: <FaToolbox /> },
              },
              {
                name: "vessel-data-transfer",
                list: "/vessel-data-transfer",
                create: "/vessel-data-transfer/create",
                meta: { parent: "emr-vessel-services", label: "Data Transfer", icon: <FaUpload /> },
              },
              {
                name: "avcs-incremental-update",
                list: "/avcs-incremental-update",
                create: "/avcs-incremental-update/create",
                meta: { parent: "emr-vessel-services", label: "AVCS Data Request", icon: <FaCloudDownloadAlt /> },
              },
              {
                name: "global-settings",
                list: "/global-settings",
                meta: { parent: "emr-settings", label: "Application", icon: <FaCog /> },
              },
              {
                name: "vessel-email-settings",
                list: "/vessel-email-settings",
                meta: { parent: "emr-settings", label: "Email Settings", icon: <FaEnvelope /> },
              },
              {
                name: "audit-logs",
                list: "/audit-logs",
                meta: { parent: "emr-settings", label: "Audit Logs", icon: <AuditOutlined /> },
              },
              {
                name: "vessel-accounts",
                list: "/vessel-accounts",
                meta: { parent: "emr-settings", label: "Vessel Accounts", icon: <FaUserLock /> },
              },
              {
                name: "address",
                list: "/address",
                create: "/address/create",
                edit: "/address/edit/:id",
                show: "/address/show/:id",
                meta: { parent: "emr-settings", icon: <FaAddressCard /> },
              },
              {
                name: "contact",
                list: "/contact",
                create: "/contact/create",
                edit: "/contact/edit/:id",
                show: "/contact/show/:id",
                meta: { parent: "emr-settings", icon: <FaUserTie /> },
              },
              {
                name: "country",
                list: "/country",
                create: "/country/create",
                edit: "/country/edit/:id",
                show: "/country/show/:id",
                meta: { parent: "emr-settings", icon: <FaGlobeAmericas /> },
              },
              {
                name: "currency",
                list: "/currency",
                create: "/currency/create",
                edit: "/currency/edit/:id",
                show: "/currency/show/:id",
                meta: { parent: "emr-settings", icon: <FaCoins /> },
              },
              {
                name: "vessel-resource-holdings",
                create: "/vessel-resource-holdings/create",
                meta: { label: "Holdings Report" },
              },
            ]}
            notificationProvider={useNotificationProvider}
            dataProvider={dataProvider(axiosInstance)}
            authProvider={authProvider(axiosInstance)}
            accessControlProvider={accessControlProvider(axiosInstance)}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
            }}
          >
            <Routes>
              <Route
                element={
                  <Authenticated key="auth" fallback={<LoginRedirect />}>
                    <ThemedLayout
                      Header={Header}
                      initialSiderCollapsed
                      Sider={() => (
                        <ConfigProvider theme={siderTheme}>
                          <ThemedSider Title={Title} fixed />
                        </ConfigProvider>
                      )}
                    >
                      <Outlet />
                    </ThemedLayout>
                  </Authenticated>
                }
              >
                <Route index element={<NavigateToResource resource="dashboard" />} />
                <Route path="/dashboard" element={<Dashboard />} />

                {/* Permit Routes */}
                <Route path="/upload-vessel-permits" element={<UploadVesselPermitsPage />} />
                <Route path="/upload-seall-permits" element={<UploadSeallPermitsPage />} />
                <Route path="/upload-fetch-permits" element={<UploadFetchPermitsPage />} />

                {/* Settings Routes */}
                <Route path="/global-settings" element={<GlobalSettingsPage />} />
                <Route path="/vessel-email-settings" element={<VesselEmailSettingsPage />} />
                <Route path="/audit-logs" element={<AuditLogList />} />
                <Route path="/vessel-accounts" element={<VesselAccounts />} />

                {/* Address */}
                <Route path="/address">
                  <Route index element={<AddressList />} />
                  <Route path="create" element={<AddressCreate />} />
                  <Route path="edit/:id" element={<AddressEdit />} />
                  <Route path="show/:id" element={<AddressShow />} />
                </Route>

                {/* Contact */}
                <Route path="/contact">
                  <Route index element={<ContactList />} />
                  <Route path="create" element={<ContactCreate />} />
                  <Route path="edit/:id" element={<ContactEdit />} />
                  <Route path="show/:id" element={<ContactShow />} />
                </Route>

                {/* Country */}
                <Route path="/country">
                  <Route index element={<CountryList />} />
                  <Route path="create" element={<CountryCreate />} />
                  <Route path="edit/:id" element={<CountryEdit />} />
                  <Route path="show/:id" element={<CountryShow />} />
                </Route>

                {/* Currency */}
                <Route path="/currency">
                  <Route index element={<CurrencyList />} />
                  <Route path="create" element={<CurrencyCreate />} />
                  <Route path="edit/:id" element={<CurrencyEdit />} />
                  <Route path="show/:id" element={<CurrencyShow />} />
                </Route>

                {/* Shipco */}
                <Route path="/shipco">
                  <Route index element={<ShipcoList />} />
                  <Route path="create" element={<ShipcoCreate />} />
                  <Route path="edit/:id" element={<ShipcoEdit />} />
                  <Route path="show/:id" element={<ShipcoShow />} />
                </Route>

                {/* Shipco Group */}
                <Route path="/shipco-group">
                  <Route index element={<ShipcoGroupList />} />
                  <Route path="create" element={<ShipcoGroupCreate />} />
                  <Route path="edit/:id" element={<ShipcoGroupEdit />} />
                  <Route path="show/:id" element={<ShipcoGroupShow />} />
                </Route>

                {/* Licence */}
                <Route path="/licence">
                  <Route index element={<LicenceList />} />
                  <Route path="create" element={<LicenceCreate />} />
                  <Route path="edit/:id" element={<LicenceEdit />} />
                  <Route path="show/:id" element={<LicenceShow />} />
                </Route>

                {/* Vessel */}
                <Route path="/vessel">
                  <Route index element={<VesselList />} />
                  <Route path="create" element={<VesselCreate />} />
                  <Route path="edit/:id" element={<VesselEdit />} />
                  <Route path="show/:id" element={<VesselShow />} />
                </Route>

                {/* ECDIS */}
                <Route path="/ecdis">
                  <Route index element={<EcdisList />} />
                  <Route path="create" element={<EcdisCreate />} />
                  <Route path="edit/:id" element={<EcdisEdit />} />
                  <Route path="show/:id" element={<EcdisShow />} />
                </Route>

                {/* eNTM Vessels */}
                <Route path="/entm-vessels">
                  <Route index element={<EntmVesselsList />} />
                  <Route path="create" element={<EntmVesselsCreate />} />
                  <Route path="edit/:id" element={<EntmVesselsEdit />} />
                  <Route path="show/:id" element={<EntmVesselsShow />} />
                </Route>

                {/* Bulk Email */}
                <Route path="/bulk-email">
                  <Route index element={<BulkEmailJobsList />} />
                  <Route path="show/:id" element={<BulkEmailJobsShow />} />
                </Route>

                {/* Vessel Transfer */}
                <Route path="/vessel-transfer">
                  <Route index element={<VesselTransferList />} />
                  <Route path="create" element={<VesselTransferCreate />} />
                  <Route path="show/:id" element={<VesselTransferShow />} />
                </Route>

                {/* Subscription */}
                <Route path="/subscription">
                  <Route index element={<SubscriptionList />} />
                  <Route path="create" element={<SubscriptionCreate />} />
                  <Route path="edit/:id" element={<SubscriptionEdit />} />
                  <Route path="show/:id" element={<SubscriptionShow />} />
                </Route>

                {/* AVCS Service */}
                <Route path="/metadata-avcs">
                  <Route index element={<MetadataAvcsList />} />
                  <Route path="create" element={<MetadataAvcsCreate />} />
                  <Route path="edit/:id" element={<MetadataAvcsEdit />} />
                  <Route path="show/:id" element={<MetadataAvcsShow />} />
                </Route>

                {/* ADP Service */}
                <Route path="/metadata-adp">
                  <Route index element={<MetadataAdpList />} />
                  <Route path="create" element={<MetadataAdpCreate />} />
                  <Route path="edit/:id" element={<MetadataAdpEdit />} />
                  <Route path="show/:id" element={<MetadataAdpShow />} />
                </Route>

                {/* ENP Service */}
                <Route path="/metadata-enp">
                  <Route index element={<MetadataEnpList />} />
                  <Route path="create" element={<MetadataEnpCreate />} />
                  <Route path="edit/:id" element={<MetadataEnpEdit />} />
                  <Route path="show/:id" element={<MetadataEnpShow />} />
                </Route>

                {/* ARCS Service */}
                <Route path="/metadata-arcs">
                  <Route index element={<MetadataArcsList />} />
                  <Route path="create" element={<MetadataArcsCreate />} />
                  <Route path="edit/:id" element={<MetadataArcsEdit />} />
                  <Route path="show/:id" element={<MetadataArcsShow />} />
                </Route>

                {/* Other Services */}
                <Route path="/metadata-other">
                  <Route index element={<MetadataOtherList />} />
                  <Route path="create" element={<MetadataOtherCreate />} />
                  <Route path="edit/:id" element={<MetadataOtherEdit />} />
                  <Route path="show/:id" element={<MetadataOtherShow />} />
                </Route>

                {/* Order Routes */}
                <Route path="/upload-orders" element={<UploadOrdersPage />} />
                <Route path="/order">
                  <Route index element={<OrderList />} />
                  <Route path="show/:id" element={<OrderShow />} />
                </Route>
                <Route path="/order-upload-status" element={<OrderUploadStatusList />} />

                {/* Report Routes */}
                <Route path="/vessel-spend-report" element={<VesselSpendReportList />} />
                <Route path="/invoice-report" element={<InvoiceReportList />} />
                <Route path="/invoice-data">
                  <Route index element={<InvoiceDataList />} />
                  <Route path="create" element={<InvoiceDataCreate />} />
                </Route>
                <Route path="/physical-books">
                  <Route index element={<PhysicalBooksList />} />
                  <Route path="create" element={<PhysicalBooksCreate />} />
                </Route>
                <Route path="/vessel-certificates">
                  <Route index element={<VesselCertificatesList />} />
                  <Route path="create" element={<VesselCertificatesCreate />} />
                </Route>
                <Route path="/ukho-invoice-report" element={<UkhoInvoiceReportList />} />
                <Route path="/order-report" element={<OrderReportList />} />
                <Route path="/on-demand-reports" element={<OnDemandReportsList />} />

                {/* Bookshelf Routes */}
                <Route path="/digital-publication">
                  <Route index element={<DigitalPublicationList />} />
                  <Route path="create" element={<DigitalPublicationCreate />} />
                </Route>
                <Route path="/enp-data" element={<EnpDataList />} />
                <Route path="/flag-circular">
                  <Route index element={<FlagStateCircularList />} />
                  <Route path="create" element={<FlagStateCircularCreate />} />
                </Route>
                <Route path="/notices-mariners">
                  <Route index element={<NoticesMarinersList />} />
                  <Route path="create" element={<NoticesMarinersCreate />} />
                </Route>
                <Route path="/nav-warnings">
                  <Route index element={<NavWarningsList />} />
                  <Route path="create" element={<NavWarningsCreate />} />
                </Route>

                {/* Standalone Routes */}
                <Route path="/vessel-resource">
                  <Route index element={<VesselResourcesList />} />
                  <Route path="create" element={<VesselResourceCreate />} />
                </Route>
                <Route path="/vessel-resource-holdings">
                  <Route path="create" element={<HoldingsReportPage />} />
                </Route>
                <Route path="/vessel-startup-kit">
                  <Route index element={<VesselStartupKitList />} />
                  <Route path="create" element={<VesselStartupKitCreate />} />
                </Route>
                <Route path="/vessel-data-transfer">
                  <Route index element={<VesselDataTransferList />} />
                  <Route path="create" element={<VesselDataTransferCreate />} />
                </Route>
                <Route path="/avcs-incremental-update">
                  <Route index element={<IncrementalUpdateList />} />
                  <Route path="create" element={<IncrementalUpdateCreate />} />
                </Route>

                {/* Vessel Data Verifications */}
                <Route path="/vessel-verifications">
                  <Route index element={<VesselDataVerificationsList />} />
                  <Route path=":publicId/approve" element={<VesselDataVerificationApprove />} />
                </Route>
              </Route>

              <Route
                element={
                  <Authenticated key="auth-login" fallback={<Outlet />}>
                    <AlreadyAuthedRedirect />
                  </Authenticated>
                }
              >
                <Route path="/login" element={<Login />} />
              </Route>

              <Route path="*" element={<ErrorComponent />} />
            </Routes>
          </Refine>
        </BrowserRouter>
      </AntdApp>
    </ConfigProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
}

export default App;
